import { api } from '../api';
import type { MenuItem } from '@/types/menu-item';
import type { Menu } from '@/types/menu';
import type { PaginatedResponse, PaginationParams } from '@/types/common';

export interface MenuItemSearchParams extends PaginationParams {
  menuId?: number;
  companyId?: number;
  /** Only items in this menu category. */
  categoryId?: number;
}

export interface MenuSearchParams extends PaginationParams {
  companyId?: number;
}

export interface MenuItemRecipeInput {
  recipeId: number;
  quantity?: number;
}

export interface CreateMenuItemInput {
  name: string;
  menuId: number;
  price?: number;
  description?: string;
  /** A menu category of one of the item's concepts. The API keeps the category name in step. */
  categoryId?: number;
  station?: string;
  isFeatured?: boolean;
  isAvailable?: boolean;
  recipes: MenuItemRecipeInput[];
  /**
   * The concepts that serve this item. Omit to let the API inherit from the
   * menu — its concept, or every active concept when the menu is org-wide.
   */
  conceptIds?: number[];
}

export type MenuType =
  | 'BREAKFAST'
  | 'LUNCH'
  | 'DINNER'
  | 'ALL_DAY'
  | 'SEASONAL'
  | 'SPECIAL';

export interface CreateMenuInput {
  name: string;
  type: MenuType;
  description?: string;
  companyId?: number;
}

export interface UpdateMenuItemInput {
  name?: string;
  /** Move the item to a different menu in the same organization. */
  menuId?: number;
  price?: number;
  description?: string;
  /** `null` takes the item out of its category. */
  categoryId?: number | null;
  station?: string;
  isFeatured?: boolean;
  isAvailable?: boolean;
  recipes?: MenuItemRecipeInput[];
  /** If provided, replaces the full set of concepts that serve this item. */
  conceptIds?: number[];
}

function buildSearchParams(params?: MenuItemSearchParams): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  if (params.page != null) sp.set('page', params.page.toString());
  if (params.limit != null) sp.set('limit', params.limit.toString());
  if (params.sortBy) sp.set('sortBy', params.sortBy);
  if (params.sortOrder) sp.set('sortOrder', params.sortOrder);
  if (params.search) sp.set('search', params.search);
  if (params.menuId != null) sp.set('menuId', params.menuId.toString());
  if (params.companyId != null) sp.set('companyId', params.companyId.toString());
  if (params.categoryId != null) sp.set('categoryId', params.categoryId.toString());
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

export const menuItemRequests = {
  list: (params?: MenuItemSearchParams) =>
    api.get<PaginatedResponse<MenuItem>>(`/menu-items${buildSearchParams(params)}`),

  getById: (id: number | string) => api.get<MenuItem>(`/menu-items/${id}`),

  // skipToast: these mutations surface the server message via their own
  // onError handlers (getApiErrorMessage). The client's generic auto-toast
  // (handleErrorToast) fires for 403/404/409/5xx, so without skipToast those
  // would double up. (It already stays silent on 400/401, so nothing is lost
  // there.)
  create: (data: CreateMenuItemInput) =>
    api.post<MenuItem>('/menu-items', data, { skipToast: true }),

  update: (id: number | string, data: UpdateMenuItemInput) =>
    api.patch<MenuItem>(`/menu-items/${id}`, data, { skipToast: true }),

  delete: (id: number | string) => api.delete(`/menu-items/${id}`, { skipToast: true }),

  /** Menus available to attach a menu item to. */
  listMenus: (params?: MenuSearchParams) => {
    const query = new URLSearchParams();
    if (params?.companyId != null) query.set('companyId', String(params.companyId));
    const qs = query.toString();
    return api.get<PaginatedResponse<Menu>>(`/menus${qs ? `?${qs}` : ''}`);
  },

  createMenu: (data: CreateMenuInput) =>
    api.post<Menu>('/menus', data, { skipToast: true }),
};
